import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';

import {
  isSalesMember,
  isServiceClient,
  getStatusBySlug,
  makeOdooOpportunityLink,
  makeHubspotDealLink,
  makeOdooContactLink,
} from '@greenkub-fronts/shared/utils';
import { useGetProjectByIdQuery } from '@greenkub-fronts/data-access';
import { GROUP_NAME, RootState } from '@greenkub-fronts/shared/interface';

import styles from './project-navigation-bar.module.scss';

import EmployeesModal from '../EmployeesModal/employees-modal';
import NotificationsModalFeature from '../NotificationsModal/notifications-modal-feature';
import Card from '../Card/card';
import Button, { BtnLevel, BtnType } from '../Button/button';
import { Icon, IconColor } from '../../icon/icon';
import { IconEnum } from '../../icon/icon.enum';
import { useLocation } from 'react-use';
import H1 from '../../typographie/H1/h1';

enum ProjectLocation {
  TASK = 'task',
  DOCUMENT = 'document',
  CHAT = 'chat',
  DEVIS = 'devis',
  INFO = 'info',
  PLANNING = 'planning',
  FORMULAIRE = 'form',
  TECHNICAL_VISIT = 'visit',
  PRODUCTION = 'production',
  TRANSPORT = 'transport',
  INCIDENT = 'incident',
  WORKSITE = 'worksite',
  BUILD_RESERV = 'reserv',
  SAV = 'sav',
}

export interface ProjectNavigationBarProps {
  baseUrl?: string;
}

export function ProjectNavigationBar(props: ProjectNavigationBarProps) {
  const { baseUrl = '' } = props;

  const { projectId = '' } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const { user } = useSelector((state: RootState) => state.auth);
  const { isCrm } = useSelector((state: RootState) => state.ui);

  const [isOpenEmployeesModal, setIsOpenEmployeesModal] =
    useState<boolean>(false);

  const [isNotificationsModalOpened, setIsNotificationsModalOpened] =
    useState<boolean>(false);

  const { data: project } = useGetProjectByIdQuery({
    projectId: parseInt(projectId),
    groupName: GROUP_NAME.FULL_PROJECT,
  });
  const location = useLocation();

  if (project === undefined) return <></>;

  return (
    <>
      {isCrm && (
        <div className={styles['InfoBar']}>
          <H1>
            <span>Projet {project.isCCMI && <strong>CCMI </strong>}{project.id}</span>
          </H1>

          <div className={styles['HeadLeft']}>
            <span>Opportunité </span>
            <a
              target="_blank"
              rel="noreferrer"
              href={makeOdooOpportunityLink(project.odooId)}
            >
              <span className="odooIdBullet">#{project.odooId || 'vide'}</span>
            </a>
            du contact
            <a
              target="_blank"
              rel="noreferrer"
              href={makeOdooContactLink(project.customer?.odooId)}
            >
              <span className="odooIdBullet">
                #{project.customer?.odooId || 'vide'}
              </span>
            </a>
            {project.customer?.firstName} {project.customer?.lastName}
          </div>

          <p style={{ fontSize: '1.8rem' }}>
            <span className="mr5">Transaction Hubspot</span>
            <a
              target="_blank"
              rel="noreferrer"
              href={makeHubspotDealLink(project.hubspotId)}
            >
              <span className="odooIdBullet">
                {project.hubspotId || 'vide'}
              </span>
            </a>
          </p>

          <span className={styles['Status']}>
            <span
              className={
                'Project-status-bull status-' + (project ? project.status : '')
              }
            />
            {'Statut : ' + getStatusBySlug(project ? project.status : '')}
          </span>
        </div>
      )}

      <Card className={styles['NavigationBar']}>
        <>
          {isCrm && (
            <div>
              <Button
                onClick={() =>
                  navigate(`${baseUrl}/customer/${project.customer.id}`)
                }
                label="Projet du même client"
                type={BtnType.LINEAR}
                level={BtnLevel.SECONDARY}
              />
            </div>
          )}

          <div className={styles['NavigationBar-middle']}>
            <div className={styles['NavigationBar-Line']}>
              <Icon
                className={styles['icon-button']}
                name={IconEnum.EMPLOYEE_TITLED}
                onClick={() => {
                  setIsOpenEmployeesModal(true);
                }}
                coloricon={IconColor.GRAY}
              />

              {isCrm && (
                <Icon
                  onClick={() => navigate(`${baseUrl}/task/${project.id}`)}
                  onAuxClick={() =>
                    window.open(`${baseUrl}/task/${project.id}`, '_blank')
                  }
                  name={IconEnum.TASK_TITLED}
                  className={styles['icon-button']}
                  coloricon={
                    location.pathname &&
                    location.pathname.includes(ProjectLocation.TASK)
                      ? IconColor.GREEN
                      : IconColor.GRAY
                  }
                />
              )}

              <Icon
                onClick={() => navigate(`${baseUrl}/document/${project.id}`)}
                onAuxClick={() =>
                  window.open(`${baseUrl}/document/${project.id}`, '_blank')
                }
                name={IconEnum.DOCS_TITLED}
                className={styles['icon-button']}
                coloricon={
                  location.pathname &&
                  location.pathname.includes(ProjectLocation.DOCUMENT)
                    ? IconColor.GREEN
                    : IconColor.GRAY
                }
              />

              {isCrm && (isServiceClient(user) || isSalesMember(user)) && (
                <Icon
                  onClick={() => navigate(`${baseUrl}/chat/${project.id}`)}
                  onAuxClick={() => {
                    window.open(`${baseUrl}/chat/${project.id}`, '_blank');
                  }}
                  name={IconEnum.CHAT_TITLED}
                  className={styles['icon-button']}
                  coloricon={
                    location.pathname &&
                    location.pathname.includes(ProjectLocation.CHAT)
                      ? IconColor.GREEN
                      : IconColor.GRAY
                  }
                />
              )}

              {isCrm && (
                <Icon
                  onClick={() => navigate(`${baseUrl}/quote/${project.id}/1`)}
                  onAuxClick={() => {
                    window.open(`${baseUrl}/quote/${project.id}/1`, '_blank');
                  }}
                  name={IconEnum.QUOTE_TITLED}
                  className={styles['icon-button']}
                  coloricon={
                    location.pathname &&
                    location.pathname.includes(ProjectLocation.DEVIS)
                      ? IconColor.GREEN
                      : IconColor.GRAY
                  }
                />
              )}

              <Icon
                onClick={() => {
                  navigate(`${baseUrl}/info/${project.id}`);
                }}
                onAuxClick={() =>
                  window.open(`${baseUrl}/info/${project.id}`, '_blank')
                }
                name={IconEnum.INFO_TITLED}
                className={styles['icon-button']}
                coloricon={
                  location.pathname &&
                  location.pathname.includes(ProjectLocation.INFO)
                    ? IconColor.GREEN
                    : IconColor.GRAY
                }
              />
            </div>
          </div>

          {isCrm && (
            <div>
              <Button
                onClick={() => navigate(`${baseUrl}/config/${project.id}`)}
                level={
                  project.bdcSignedAt ? BtnLevel.SECONDARY : BtnLevel.INACTIF
                }
                IconeRight={IconEnum.SHOPPING_CART}
                label="Panier"
              />
            </div>
          )}
        </>
      </Card>

      <EmployeesModal
        project={project}
        isOpen={isOpenEmployeesModal}
        onChangeIsOpen={setIsOpenEmployeesModal}
      />

      {isNotificationsModalOpened && (
        <NotificationsModalFeature
          isOpen={isNotificationsModalOpened}
          onChangeIsOpen={setIsNotificationsModalOpened}
          projectId={project.id}
        />
      )}
    </>
  );
}

export default ProjectNavigationBar;
